
import { CommandPattern, CommandType } from './voiceCommandUtils';

interface WeightedMatch {
  score: number;
  match: RegExpMatchArray | null;
  pattern: CommandPattern;
}

/** Synonym groups by intent */
const SYNONYMS: Record<string, string[]> = {
  navigation: ['go', 'navigate', 'open', 'show', 'display', 'take me', 'jump', 'head'],
  back: ['back', 'go back', 'previous', 'return'],
  help: ['help', 'support', 'what can you do'],
  search: ['search', 'find', 'look', 'query'],
  darkMode: ['dark mode', 'night mode'],
  enable: ['enable', 'turn on', 'activate'],
  disable: ['disable', 'turn off', 'deactivate'],
};

function expandSynonyms(str: string): string {
  let expanded = str;
  Object.entries(SYNONYMS).forEach(([_, syngroup]) => {
    syngroup.forEach(synonym => {
      // Replace synonyms with canonical (just spaces, not word boundaries -- loose match)
      expanded = expanded.replace(new RegExp(`\\b${synonym}\\b`, 'gi'), syngroup[0]);
    });
  });
  return expanded;
}

/**
 * Basic fuzzy string similarity (Levenshtein-like, normalized to [0,1])
 */
function similarity(a: string, b: string): number {
  a = a.toLowerCase();
  b = b.toLowerCase();
  if (a === b) return 1;
  if (!a.length || !b.length) return 0;
  let longer = a.length >= b.length ? a : b;
  let shorter = a.length < b.length ? a : b;
  let edits = longer.length - shorter.length;
  for (let i = 0; i < shorter.length; i++) {
    if (shorter[i] !== longer[i]) edits++;
  }
  return 1 - edits / longer.length;
}

/**
 * Find all command pattern weighted matches based on match, synonym, and fuzzy score
 */
export function findBestPatternMatch(
  command: string,
  patterns: CommandPattern[]
): WeightedMatch | null {
  const normalized = expandSynonyms(command.trim().replace(/\s+/g, ' ').toLowerCase());

  let best: WeightedMatch | null = null;

  for (const patternObj of patterns) {
    // Expand synonyms in pattern regex (by rebuilding with mapped alternatives)
    const testRegex =
      patternObj.pattern instanceof RegExp
        ? new RegExp(patternObj.pattern.source, 'i')
        : new RegExp(patternObj.pattern, 'i');

    // Try direct match
    let match = normalized.match(testRegex);
    let score = 0;
    if (match) {
      // High score for direct regex match
      score = 1.0;
    } else {
      // Fuzzy check (compare command to canonical keywords in pattern)
      // Use pattern type for more context
      // Try to extract key intent words for fuzzy matching
      const canon = patternObj.type;
      score = similarity(normalized, canon);
      // Lower but valid score for close match
      match = null;
    }

    // If compound command triggers (see below) - never makes sense to match partially
    if (score > 0.5) {
      if (!best || score > best.score) {
        best = { score, match, pattern: patternObj };
      }
    }
  }
  return best;
}

/**
 * Split command text at major "and"/"then" conjunctions for compound support.
 */
export function splitCompoundCommands(command: string): string[] {
  // Recognize: "do X and Y", "do X then Y"
  return command
    .split(/\b(?:and|then|&)\b/gi)
    .map(s => s.trim())
    .filter(Boolean);
}
